import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChatCircleDots, MagnifyingGlass, ArrowsClockwise, Prohibit, CheckCircle, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { confirmDialog } from '../components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function ViberInboundPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('inbox');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const [r1, r2] = await Promise.all([
        api.get('/viber-inbound', { params }),
        api.get('/viber-blocked'),
      ]);
      setItems(r1.data.items || []);
      setTotal(r1.data.total || 0);
      setBlocked(r2.data.items || []);
    } catch {
      toast.error('Greska pri ucitavanju');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = {};
        const [r1, r2] = await Promise.all([
          api.get('/viber-inbound', { params }),
          api.get('/viber-blocked'),
        ]);
        setItems(r1.data.items || []);
        setTotal(r1.data.total || 0);
        setBlocked(r2.data.items || []);
      } catch {
        toast.error('Greska pri ucitavanju');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/viber-inbound/${id}/read`);
      setItems(prev => prev.map(it => it.id === id ? { ...it, read: true } : it));
    } catch { toast.error('Greska'); }
  };

  const blockPhone = async (phone) => {
    if (!(await confirmDialog(`Blokirati broj ${phone} od slanja Viber poruka iz CRM-a?\n\nUbuduće CRM neće moći da pošalje Viber na ovaj broj. Mogu se ponovo odblokirati u sekciji "Blokirani brojevi".`))) return;
    try {
      await api.post(`/viber-blocked/${phone}`);
      toast.success('Broj blokiran');
      load();
    } catch { toast.error('Greska'); }
  };

  const unblock = async (phone) => {
    try {
      await api.delete(`/viber-blocked/${phone}`);
      toast.success('Broj odblokiran');
      load();
    } catch { toast.error('Greska'); }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('sr-Latn-ME', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="viber-inbound-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ChatCircleDots size={26} weight="duotone" className="text-purple-600" />Primljene Viber poruke</h1>
          <p className="text-sm text-slate-500">Odgovori klijenata na Vaše PDV obavještenja i ostale Viber poruke.</p>
        </div>
        <Button onClick={load} variant="outline" data-testid="viber-inbound-refresh"><ArrowsClockwise size={16} className="mr-2" />Osvjezi</Button>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setTab('inbox')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'inbox' ? 'border-purple-600 text-purple-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid="tab-inbox">
          Inbox ({total})
        </button>
        <button onClick={() => setTab('blocked')} className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'blocked' ? 'border-red-500 text-red-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid="tab-blocked">
          Blokirani brojevi ({blocked.length})
        </button>
      </div>

      {tab === 'inbox' && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex gap-3 items-center">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
                  placeholder="Pretraga po broju, tekstu ili nazivu klijenta..." className="pl-10 bg-white border-slate-300" data-testid="viber-in-search" />
              </div>
              <Button onClick={load} className="bg-purple-600 hover:bg-purple-700 text-white">Filtriraj</Button>
            </div>

            <div className="space-y-3">
              {loading ? <p className="text-center text-slate-400 py-8">Učitavanje...</p>
                : items.length === 0 ? <p className="text-center text-slate-400 py-8">Nema primljenih Viber poruka</p>
                : items.map(it => (
                  <div key={it.id} className={`p-4 rounded-lg border transition ${it.read ? 'bg-white border-slate-200' : 'bg-purple-50 border-purple-200'}`} data-testid={`viber-in-${it.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900">{it.client_name || 'Nepoznat klijent'}</span>
                        <span className="text-xs text-slate-500 font-mono">{it.from_phone}</span>
                        {!it.read && <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-xs">Novo</Badge>}
                      </div>
                      <div className="flex gap-1">
                        {!it.read && <Button size="sm" variant="ghost" onClick={() => markRead(it.id)} title="Označi kao pročitano"><CheckCircle size={14} className="text-green-600" /></Button>}
                        <Button size="sm" variant="ghost" onClick={() => blockPhone(it.from_phone)} title="Blokiraj ovaj broj"><Prohibit size={14} className="text-red-600" /></Button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed pl-1">{it.text}</p>
                    <div className="text-xs text-slate-400 mt-2 pl-1">{fmt(it.received_at)}</div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'blocked' && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-600 mb-4">Brojevi na ovoj listi neće dobiti Viber poruke iz CRM-a. Email i drugi kanali rade normalno.</p>
            {blocked.length === 0 ? <p className="text-center text-slate-400 py-8">Nema blokiranih brojeva</p>
              : <div className="space-y-2">
                {blocked.map(b => (
                  <div key={b.phone} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-md">
                    <div>
                      <div className="font-mono font-semibold text-slate-900">{b.phone}</div>
                      <div className="text-xs text-slate-500">Blokirano {fmt(b.blocked_at)} od strane {b.blocked_by}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => unblock(b.phone)} className="border-red-300 text-red-700 hover:bg-red-100" data-testid={`unblock-${b.phone}`}>
                      <Trash size={14} className="mr-1" />Odblokiraj
                    </Button>
                  </div>
                ))}
              </div>
            }
          </CardContent>
        </Card>
      )}
    </div>
  );
}
